import api from './api';

class FileUploadService {
  /**
   * Upload a single file
   * @param {File} file - The file to upload
   * @param {Object} options - Upload options
   * @param {string} options.projectId - Project ID (optional)
   * @param {string} options.conversationId - Conversation ID (optional)
   * @param {string} options.description - File description (optional)
   * @param {string} options.purpose - File purpose (default: 'content')
   * @param {Function} options.onProgress - Progress callback (optional)
   * @returns {Promise} Upload response
   */
  async uploadFile(file, options = {}) {
    const {
      projectId,
      conversationId,
      description,
      purpose = 'content',
      onProgress
    } = options;

    // Validate file size (configurable limit from environment)
    const maxSizeMB = parseInt(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 100;
    const maxSize = maxSizeMB * 1024 * 1024; // Convert MB to bytes
    if (file.size > maxSize) {
      throw new Error(`File size must be less than ${maxSizeMB}MB. Current size: ${this.formatFileSize(file.size)}`);
    }

    // Validate file type
    if (!this.isFileTypeSupported(file.name)) {
      throw new Error(`File type not supported: ${this.getFileExtension(file.name)}`);
    }

    const formData = new FormData();
    formData.append('file', file);
    
    if (projectId) formData.append('project_id', projectId);
    if (conversationId) formData.append('conversation_id', conversationId);
    if (description) formData.append('description', description);
    formData.append('purpose', purpose);

    try {
      const response = await api.post('/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      });

      return response.data;
    } catch (error) {
      console.error('File upload error:', error);
      throw this.handleUploadError(error);
    }
  }

  /**
   * Upload multiple files
   * @param {File[]} files - Array of files to upload
   * @param {Object} options - Upload options
   * @returns {Promise} Upload responses
   */
  async uploadMultipleFiles(files, options = {}) {
    const {
      projectId,
      conversationId,
      purpose = 'content',
      onProgress,
      onFileProgress
    } = options;

    // Validate file count
    if (files.length > 10) {
      throw new Error('Maximum 10 files can be uploaded at once');
    }

    // Validate total size
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const maxTotalSize = 100 * 1024 * 1024; // 100MB
    if (totalSize > maxTotalSize) {
      throw new Error(`Total file size must be less than 100MB. Current total: ${this.formatFileSize(totalSize)}`);
    }

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    if (projectId) formData.append('project_id', projectId);
    if (conversationId) formData.append('conversation_id', conversationId);
    formData.append('purpose', purpose);

    try {
      const response = await api.post('/files/upload-multiple', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress(percentCompleted);
          }
        },
      });

      return response.data;
    } catch (error) {
      console.error('Multiple file upload error:', error);
      throw this.handleUploadError(error);
    }
  }

  /**
   * Get list of uploaded files
   * @param {Object} filters - Filter options
   * @returns {Promise} File list response
   */
  async getFiles(filters = {}) {
    const {
      projectId,
      conversationId,
      fileType,
      page = 1,
      limit = 20
    } = filters;

    const params = new URLSearchParams();
    if (projectId) params.append('project_id', projectId);
    if (conversationId) params.append('conversation_id', conversationId);
    if (fileType) params.append('file_type', fileType);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    try {
      const response = await api.get(`/files/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Get files error:', error);
      throw error;
    }
  }

  /**
   * Get file details
   * @param {string} fileId - File ID
   * @returns {Promise} File details
   */
  async getFileDetails(fileId) {
    try {
      const response = await api.get(`/files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error('Get file details error:', error);
      throw error;
    }
  }

  /**
   * Download a file
   * @param {string} fileId - File ID
   * @param {string} filename - Original filename
   * @returns {Promise} Blob for download
   */
  async downloadFile(fileId, filename) {
    try {
      const response = await api.get(`/files/${fileId}/download`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return response.data;
    } catch (error) {
      console.error('Download file error:', error);
      throw error;
    }
  }

  /**
   * Get Claude content block for file
   * @param {string} fileId - File ID
   * @returns {Promise} Claude content block
   */
  async getClaudeContent(fileId) {
    try {
      const response = await api.get(`/files/${fileId}/claude-content`);
      return response.data;
    } catch (error) {
      console.error('Get Claude content error:', error);
      throw error;
    }
  }

  /**
   * Delete a file
   * @param {string} fileId - File ID
   * @returns {Promise} Delete response
   */
  async deleteFile(fileId) {
    try {
      const response = await api.delete(`/files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error('Delete file error:', error);
      throw error;
    }
  }

  /**
   * Get file upload statistics
   * @param {string} projectId - Project ID (optional)
   * @returns {Promise} File statistics
   */
  async getFileStats(projectId = null) {
    const params = projectId ? `?project_id=${projectId}` : '';
    try {
      const response = await api.get(`/files/stats/summary${params}`);
      return response.data;
    } catch (error) {
      console.error('Get file stats error:', error);
      throw error;
    }
  }

  /**
   * Get supported file types
   * @returns {Promise} Supported file types and limits
   */
  async getSupportedFileTypes() {
    try {
      const response = await api.get('/files/types/supported');
      return response.data;
    } catch (error) {
      console.error('Get supported file types error:', error);
      throw error;
    }
  }

  /**
   * Check if file type is supported
   * @param {string} filename - File name
   * @returns {boolean} True if supported
   */
  isFileTypeSupported(filename) {
    const extension = this.getFileExtension(filename);
    const supportedTypes = [
      // Documents
      'pdf', 'doc', 'docx', 'txt', 'md', 'rtf',
      // Images
      'png', 'jpg', 'jpeg', 'gif', 'webp',
      // Code
      'py', 'js', 'ts', 'html', 'css', 'json', 'xml', 'yaml', 'yml',
      // Data
      'csv', 'xls', 'xlsx'
    ];
    return supportedTypes.includes(extension);
  }

  /**
   * Get file extension
   * @param {string} filename - File name
   * @returns {string} File extension
   */
  getFileExtension(filename) {
    return filename.split('.').pop().toLowerCase();
  }

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file type category
   * @param {string} filename - File name
   * @returns {string} File type category
   */
  getFileTypeCategory(filename) {
    const extension = this.getFileExtension(filename);
    
    if (['pdf', 'doc', 'docx', 'txt', 'md', 'rtf'].includes(extension)) {
      return 'document';
    }
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) {
      return 'image';
    }
    if (['py', 'js', 'ts', 'html', 'css', 'json', 'xml', 'yaml', 'yml'].includes(extension)) {
      return 'code';
    }
    if (['csv', 'xls', 'xlsx'].includes(extension)) {
      return 'data';
    }
    return 'other';
  }

  /**
   * Handle upload errors
   * @param {Error} error - Upload error
   * @returns {Error} Formatted error
   */
  handleUploadError(error) {
    if (error.response?.status === 413) {
      return new Error('File size too large. Maximum allowed size is 32MB.');
    }
    if (error.response?.status === 415) {
      return new Error('File type not supported.');
    }
    if (error.response?.status === 400) {
      return new Error(error.response.data.detail || 'Invalid file upload request.');
    }
    if (error.response?.status === 401) {
      return new Error('Authentication required for file upload.');
    }
    if (error.response?.status === 429) {
      return new Error('Upload rate limit exceeded. Please try again later.');
    }
    
    return new Error('File upload failed. Please try again.');
  }

  /**
   * Validate file before upload
   * @param {File} file - File to validate
   * @returns {Object} Validation result
   */
  validateFile(file) {
    const errors = [];
    const maxSizeMB = parseInt(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 100;
    const maxSize = maxSizeMB * 1024 * 1024; // Convert MB to bytes

    if (!file) {
      errors.push('No file selected');
    } else {
      if (file.size > maxSize) {
        errors.push(`File size must be less than ${maxSizeMB}MB. Current size: ${this.formatFileSize(file.size)}`);
      }
      
      if (!this.isFileTypeSupported(file.name)) {
        errors.push(`File type not supported: ${this.getFileExtension(file.name)}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export default new FileUploadService();